import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Animated,
  Easing,
  ScrollView,
} from "react-native";
import { observer } from "mobx-react";
import { MaterialIcons, Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import Colors from "../../../Resources/Color";
import { useTranslation } from "react-i18next";
import { useDevice } from "../../_layout";
import Tooltip from "../../Components/Tooltip";
import { useCoachStore } from "../../contexts/CoachStoreContext";
import ClipboardSetRow from "./ClipboardSetRow";
import ClipboardExerciseCard from "./ClipboardExerciseCard";
import { BlockPlanificationConstants } from "../../../Resources/Constants";
import {
  shouldEnableOnlyProtocolSlotsMode,
  shouldShowOnlyProtocolSlotsToggle,
} from "../../platform/guards/coachBlockDashboardVisibility";

const BlockViewOverlay = observer(({ block, leftInset = 0 }) => {
  const { t } = useTranslation();
  const { isMobile } = useDevice();
  const coachStore = useCoachStore();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClipboardExpanded, setIsClipboardExpanded] = useState(false);
  const [hoveredCheckbox, setHoveredCheckbox] = useState(null);
  const [isShowAllHovered, setIsShowAllHovered] = useState(false);
  const [isOnlySlotsHovered, setIsOnlySlotsHovered] = useState(false);
  const [isClipboardCloseHovered, setIsClipboardCloseHovered] = useState(false);
  const animationValue = useRef(new Animated.Value(0)).current;
  const clipboardAnimationValue = useRef(new Animated.Value(0)).current;
  const clipboardVisibilityAnim = useRef(new Animated.Value(0)).current;
  const clipboardPulseAnim = useRef(new Animated.Value(0)).current;
  const clipboardPulseMountedRef = useRef(false);
  const previousClipboardRef = useRef({ sets: null, exercises: null });
  const closeTimerRef = useRef(null);
  const closeClipboardTimerRef = useRef(null);

  // Track workouts length for MobX reactivity
  const workoutsLength = block?.workouts?.length ?? 0;

  // Calculate max workout position across all weeks
  // This efficiently recalculates when workouts are added/removed or totalWeeks changes
  const maxWorkoutPosition = useMemo(() => {
    if (!block?.workouts || workoutsLength === 0) return 0;
    if (!block?.totalWeeks) return 0;

    let max = 0;
    for (let week = 1; week <= block.totalWeeks; week++) {
      const weekWorkouts = block.workouts
        .filter((w) => w.week === week)
        .sort((a, b) => a.order - b.order);
      max = Math.max(max, weekWorkouts.length);
    }
    return max;
  }, [block?.workouts, block?.totalWeeks, workoutsLength]);

  const coach = block?.client?.coach ?? block?.coach ?? coachStore;
  const blockHasProtocolSlots = !!block?.workouts?.some(
    (workout) => workout?.hasProtocolSlotExercises,
  );
  const showOnlyProtocolSlots = shouldEnableOnlyProtocolSlotsMode({
    coach,
    blockHasProtocolSlots,
  });
  const showOnlyProtocolSlotsToggle = shouldShowOnlyProtocolSlotsToggle({
    coach,
    blockHasProtocolSlots,
  });

  // Get selected positions from block (MobX observable)
  const selectedPositions = block?.selectedWorkoutPositions ?? null;
  const isOnlySlotsSelected = showOnlyProtocolSlots;

  // Check if all positions are selected (null means all)
  const isAllSelected = selectedPositions === null;

  // Animate expand/collapse
  useEffect(() => {
    Animated.timing(animationValue, {
      toValue: isExpanded ? 1 : 0,
      duration: 250,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [isExpanded, animationValue]);

  // Animate clipboard expand/collapse
  useEffect(() => {
    Animated.timing(clipboardAnimationValue, {
      toValue: isClipboardExpanded ? 1 : 0,
      duration: 250,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [isClipboardExpanded, clipboardAnimationValue]);

  // Handle position toggle
  const handlePositionToggle = useCallback(
    (position) => {
      if (!block || maxWorkoutPosition === 0 || showOnlyProtocolSlots) {
        return;
      }
      if (maxWorkoutPosition === 1) {
        return;
      }

      const current = block.selectedWorkoutPositions;
      if (current === null) {
        // Create new Set with all positions except the one being toggled
        const allPositions = new Set(
          Array.from({ length: maxWorkoutPosition }, (_, i) => i + 1),
        );
        allPositions.delete(position);
        // If only one position remains, keep it as Set (not null) to show filtered state
        block.selectedWorkoutPositions = allPositions;
      } else {
        const newSet = new Set(current);
        if (newSet.has(position)) {
          // Prevent deselecting the last remaining workout
          if (newSet.size === 1) {
            return;
          }
          newSet.delete(position);
          // If no positions selected, show all (set to null)
          block.selectedWorkoutPositions = newSet.size > 0 ? newSet : null;
        } else {
          newSet.add(position);
          // If all positions are selected, set to null for "all selected" state
          if (newSet.size === maxWorkoutPosition) {
            block.selectedWorkoutPositions = null;
          } else {
            block.selectedWorkoutPositions = newSet;
          }
        }
      }
    },
    [block, maxWorkoutPosition, showOnlyProtocolSlots],
  );

  // Clean up filter when max position decreases (e.g., after deleting workouts)
  // Remove positions from filter that are beyond the new max
  useEffect(() => {
    if (!block || maxWorkoutPosition === 0) {
      // If no workouts, reset filter to show all
      if (block && block.selectedWorkoutPositions !== null) {
        block.selectedWorkoutPositions = null;
      }
      return;
    }

    const current = block.selectedWorkoutPositions;
    if (current === null) {
      // All positions selected, no cleanup needed
      return;
    }

    // Remove positions that exceed the new max
    let needsUpdate = false;
    const newSet = new Set(current);
    newSet.forEach((position) => {
      if (position > maxWorkoutPosition) {
        newSet.delete(position);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      // If all remaining positions are selected, set to null for "all selected" state
      if (newSet.size === maxWorkoutPosition) {
        block.selectedWorkoutPositions = null;
      } else if (newSet.size === 0) {
        // If no positions remain, show all
        block.selectedWorkoutPositions = null;
      } else {
        block.selectedWorkoutPositions = newSet;
      }
    }
  }, [block, maxWorkoutPosition]);

  // Handle "Show all" button
  const handleShowAll = useCallback(() => {
    if (block) {
      block.selectedWorkoutPositions = null;
    }
    if (coach) {
      coach.showOnlyProtocolSlots = false;
    }
  }, [block, coach]);

  const handleOnlySlotsToggle = useCallback(() => {
    if (!coach || !showOnlyProtocolSlotsToggle) return;
    coach.showOnlyProtocolSlots = !coach.showOnlyProtocolSlots;
  }, [coach, showOnlyProtocolSlotsToggle]);

  // Get badge text
  const getBadgeText = useCallback(() => {
    if (isOnlySlotsSelected) {
      return t("onlySlots-string");
    }
    if (isAllSelected) {
      return t("allWorkouts-string");
    }
    if (!selectedPositions || selectedPositions.size === 0) {
      return t("allWorkouts-string");
    }
    const positions = Array.from(selectedPositions)
      .sort((a, b) => a - b)
      .map((p) => `#${p}`)
      .join(", ");
    return positions;
  }, [isAllSelected, isOnlySlotsSelected, selectedPositions, t]);

  // Handle hover/click for expansion with delay
  const handleMouseEnter = useCallback(() => {
    if (!isMobile) {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      setIsExpanded(true);
      // Close clipboard if open
      setIsClipboardExpanded(false);
    }
  }, [isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (!isMobile) {
      closeTimerRef.current = setTimeout(() => {
        setIsExpanded(false);
      }, 300); // 300ms delay to prevent accidental closing
    }
  }, [isMobile]);

  const handlePress = useCallback(() => {
    if (isMobile) {
      setIsExpanded((prev) => !prev);
      setIsClipboardExpanded(false);
    }
  }, [isMobile]);

  const closeOverlayMenus = useCallback(() => {
    setIsExpanded(false);
    setIsClipboardExpanded(false);
  }, []);

  // Handle clipboard hover/click
  const handleClipboardMouseEnter = useCallback(() => {
    if (!isMobile) {
      if (closeClipboardTimerRef.current) {
        clearTimeout(closeClipboardTimerRef.current);
        closeClipboardTimerRef.current = null;
      }
      // Optional: Auto-open on hover? The user said "when I press the button".
      // Let's stick to press for now, but allow hover to keep it open if we implement auto-open.
      // For now, just clear timer.
    }
  }, [isMobile]);

  const handleClipboardMouseLeave = useCallback(() => {
    if (!isMobile) {
      // If we want it to close on leave (like the menu), we can add this.
      // But usually clipboard is a toggle. The user said "press the button".
      // The filter menu uses hover. Let's make clipboard toggle on click, but maybe close on leave if we want consistency?
      // The user said "Follow the same UI pattern of the menu used for filtering exercises".
      // The filter menu opens on hover (desktop) and click (mobile).
      // Let's make clipboard open on click (as requested) but maybe close on outside click or toggle.
      // Actually, let's just make it toggle on click for now to be safe with "press the button".
    }
  }, [isMobile]);

  const toggleClipboard = useCallback(() => {
    setIsClipboardExpanded((prev) => {
      if (!prev) {
        setIsExpanded(false); // Close filter menu if opening clipboard
      }
      return !prev;
    });
  }, []);

  const triggerClipboardPulse = useCallback(() => {
    clipboardPulseAnim.stopAnimation(() => {
      clipboardPulseAnim.setValue(0);
      Animated.sequence([
        Animated.timing(clipboardPulseAnim, {
          toValue: 1,
          duration: 180,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(clipboardPulseAnim, {
          toValue: 0,
          duration: 260,
          easing: Easing.in(Easing.cubic),
          useNativeDriver: true,
        }),
      ]).start();
    });
  }, [clipboardPulseAnim]);

  const animatedOpacity = animationValue.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  });

  const animatedTranslateY = animationValue.interpolate({
    inputRange: [0, 1],
    outputRange: [8, 0],
  });

  const animatedScale = animationValue.interpolate({
    inputRange: [0, 1],
    outputRange: [0.95, 1],
  });

  const animatedRotation = animationValue.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "180deg"],
  });

  // Clipboard animations
  const clipboardOpacity = clipboardAnimationValue.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  });

  const clipboardTranslateY = clipboardAnimationValue.interpolate({
    inputRange: [0, 1],
    outputRange: [8, 0],
  });

  const clipboardScale = clipboardAnimationValue.interpolate({
    inputRange: [0, 1],
    outputRange: [0.95, 1],
  });

  const clipboardIconScale = clipboardPulseAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 1.12],
  });

  const clipboardIconRotate = clipboardPulseAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "-10deg"],
  });

  const clipboardPulseRingScale = clipboardPulseAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0.85, 1.3],
  });

  const clipboardPulseRingOpacity = clipboardPulseAnim.interpolate({
    inputRange: [0, 0.2, 1],
    outputRange: [0, 0.6, 0],
  });

  if (maxWorkoutPosition === 0) {
    // Don't show overlay if there are no workouts
    return null;
  }

  const showSetPriority = coach?.showSetPriority === true;
  const rirOrRpe = coach?.rirOrRpe ?? false;
  const copiedSets = coach?.copiedSets;
  const copiedExercises = coach?.copiedExercises;
  // copiedSets is an object with { protocolText: string, sets: Array }
  const hasCopiedSets =
    copiedSets &&
    typeof copiedSets === "object" &&
    Array.isArray(copiedSets.sets) &&
    copiedSets.sets.length > 0;
  const hasCopiedExercises =
    copiedExercises &&
    Array.isArray(copiedExercises) &&
    copiedExercises.length > 0;
  const hasClipboardContent = hasCopiedSets || hasCopiedExercises;

  // Mobile-specific dimensions for clipboard table
  const pillWidth = isMobile
    ? BlockPlanificationConstants.MOBILE_VALUE_PILL_WIDTH
    : BlockPlanificationConstants.VALUE_PILL_WIDTH;

  // Animate clipboard visibility (appear from left)
  useEffect(() => {
    Animated.timing(clipboardVisibilityAnim, {
      toValue: hasClipboardContent ? 1 : 0,
      duration: 300,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false, // Set to false because we're animating width
    }).start();
  }, [hasClipboardContent, clipboardVisibilityAnim]);

  // Pulse clipboard icon when new content is copied
  useEffect(() => {
    if (!clipboardPulseMountedRef.current) {
      clipboardPulseMountedRef.current = true;
      previousClipboardRef.current = {
        sets: copiedSets,
        exercises: copiedExercises,
      };
      return;
    }

    const previous = previousClipboardRef.current;
    const hasNewSets = copiedSets && copiedSets !== previous.sets;
    const hasNewExercises =
      copiedExercises && copiedExercises !== previous.exercises;

    if (hasNewSets || hasNewExercises) {
      triggerClipboardPulse();
    }

    previousClipboardRef.current = {
      sets: copiedSets,
      exercises: copiedExercises,
    };
  }, [copiedSets, copiedExercises, triggerClipboardPulse]);

  // Interpolated values for clipboard visibility animation
  const clipboardContainerOpacity = clipboardVisibilityAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  });

  const clipboardContainerWidth = clipboardVisibilityAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 32], // Collapse to 0 width when hidden, match button width when visible
  });

  const isMobileBackdropVisible =
    isMobile && (isExpanded || isClipboardExpanded);

  return (
    <View pointerEvents="box-none" style={styles.root}>
      {isMobileBackdropVisible && (
        <TouchableOpacity
          activeOpacity={1}
          delayPressIn={0}
          onPressIn={closeOverlayMenus}
          style={styles.mobileBackdrop}
        />
      )}

      <View style={[styles.container, { left: leftInset }]}>
        {/* Toolbar */}
        <View style={styles.toolbarWrapper}>
          <LinearGradient
            colors={[
              "rgba(58, 58, 60, 0.95)",
              "rgba(58, 58, 60, 0.85)",
              "rgba(66, 81, 86, 0.55)",
            ]} // More subtle gradient with bubbleColor at bottom right
            // colors={["#3A3A3C", "#3A3A3C", "rgba(57, 86, 95, 0.45)"]} // More subtle gradient with bubbleColor at bottom right

            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.toolbarGradient}
          >
            <View style={styles.clipboardWrapper}>
              <Animated.View
                pointerEvents={hasClipboardContent ? "auto" : "none"}
                style={[
                  styles.clipboardContainer,
                  {
                    opacity: clipboardContainerOpacity,
                    width: clipboardContainerWidth,
                    overflow: "hidden",
                  },
                ]}
              >
                <Tooltip
                  content="viewClipboard-string"
                  placement="top"
                  disabled={isClipboardExpanded}
                >
                  <TouchableOpacity
                    style={styles.clipboardButton}
                    activeOpacity={0.8}
                    onPress={toggleClipboard}
                  >
                    <View style={styles.clipboardIconStack}>
                      <Animated.View
                        pointerEvents="none"
                        style={[
                          styles.clipboardPulseRing,
                          {
                            opacity: clipboardPulseRingOpacity,
                            transform: [{ scale: clipboardPulseRingScale }],
                          },
                        ]}
                      />
                      <Animated.View
                        style={{
                          transform: [
                            { scale: clipboardIconScale },
                            { rotate: clipboardIconRotate },
                          ],
                        }}
                      >
                        <Ionicons
                          name="clipboard-outline"
                          size={18}
                          color={Colors.whiteColor}
                        />
                      </Animated.View>
                    </View>
                  </TouchableOpacity>
                </Tooltip>
              </Animated.View>

              {/* Clipboard Menu */}
              <Animated.View
                pointerEvents={isClipboardExpanded ? "auto" : "none"}
                style={[
                  styles.menuContainer,
                  styles.clipboardMenuContainer,
                  {
                    opacity: clipboardOpacity,
                    transform: [
                      { translateY: clipboardTranslateY },
                      { scale: clipboardScale },
                    ],
                  },
                ]}
              >
                <View style={styles.gapBridge} />
                <View style={styles.menuHeader}>
                  <Text style={styles.menuTitle}>
                    {t("clipboard-string").toUpperCase()}
                  </Text>
                  <TouchableOpacity
                    onPress={() => setIsClipboardExpanded(false)}
                    style={[
                      styles.closeButton,
                      isClipboardCloseHovered && styles.closeButtonHovered,
                    ]}
                    activeOpacity={0.8}
                    onMouseEnter={() =>
                      !isMobile && setIsClipboardCloseHovered(true)
                    }
                    onMouseLeave={() => setIsClipboardCloseHovered(false)}
                  >
                    <Ionicons
                      name="close"
                      size={14}
                      color={Colors.whiteColor}
                    />
                  </TouchableOpacity>
                </View>

                <ScrollView
                  style={styles.clipboardContent}
                  contentContainerStyle={{ gap: 12 }}
                >
                  {!hasCopiedSets && !hasCopiedExercises && (
                    <Text style={styles.emptyClipboardText}>
                      {t("emptyClipboard-string")}
                    </Text>
                  )}

                  {hasCopiedExercises && (
                    <View style={styles.clipboardSection}>
                      <Text style={styles.sectionTitle}>
                        {t("exercises-string")} ({copiedExercises.length})
                      </Text>
                      <View style={{ gap: 4 }}>
                        {copiedExercises.map((exercise, idx) => (
                          <View
                            key={exercise?.id ?? idx}
                            style={styles.clipboardExerciseWrapper}
                          >
                            <ClipboardExerciseCard exercise={exercise} />
                          </View>
                        ))}
                      </View>
                    </View>
                  )}

                  {hasCopiedSets && (
                    <View style={styles.clipboardSection}>
                      <Text style={styles.sectionTitle}>
                        {t("sets-string")} ({copiedSets.sets.length})
                      </Text>
                      <View style={[styles.setsTable, isMobile && { gap: 3 }]}>
                        <View
                          style={[
                            styles.tableHeaderRow,
                            isMobile && { gap: 6 },
                          ]}
                        >
                          <View
                            style={[
                              styles.tableHeaderCameraCell,
                              isMobile && { width: 20 },
                            ]}
                          >
                            <Ionicons
                              name="videocam-outline"
                              size={isMobile ? 11 : 12}
                              color={Colors.lightGrayColor}
                            />
                          </View>
                          {showSetPriority && (
                            <View
                              style={[
                                styles.tableHeaderPriorityCell,
                                isMobile && { width: 20 },
                              ]}
                            >
                              <Text style={styles.tableHeaderPriorityText}>
                                {t("priorityHeader-string")}
                              </Text>
                            </View>
                          )}
                          <Text
                            style={[
                              styles.tableHeaderText,
                              { width: pillWidth },
                            ]}
                          >
                            {t("weight-string")}
                          </Text>
                          <Text
                            style={[
                              styles.tableHeaderText,
                              { width: pillWidth },
                            ]}
                          >
                            {t("reps-string")}
                          </Text>
                          <Text
                            style={[
                              styles.tableHeaderText,
                              { width: pillWidth },
                            ]}
                          >
                            {rirOrRpe ? t("rir-string") : t("rpe-string")}
                          </Text>
                        </View>

                        {copiedSets.sets.map((setItem, idx) => (
                          <View
                            key={setItem?.id ?? idx}
                            style={styles.clipboardSetRowWrapper}
                          >
                            <ClipboardSetRow
                              setItem={setItem}
                              isMobile={isMobile}
                              showSetPriority={showSetPriority}
                            />
                          </View>
                        ))}
                      </View>
                    </View>
                  )}
                </ScrollView>
              </Animated.View>
            </View>

            {hasClipboardContent && <View style={styles.verticalDivider} />}

            <Tooltip
              content={
                block?.exercisesExpanded
                  ? "collapseExercises-string"
                  : "expandExercises-string"
              }
              placement="top"
            >
              <TouchableOpacity
                style={styles.expandCollapseButton}
                activeOpacity={0.7}
                onPress={() => {
                  if (block) {
                    block.exercisesExpanded = !block.exercisesExpanded;
                  }
                }}
              >
                <MaterialIcons
                  name={
                    block?.exercisesExpanded ? "unfold-less" : "unfold-more"
                  }
                  size={18}
                  color={Colors.whiteColor}
                />
              </TouchableOpacity>
            </Tooltip>

            <View style={styles.verticalDivider} />

            <View
              style={styles.selectorContainer}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <TouchableOpacity
                style={styles.selectorButton}
                onPress={handlePress}
                activeOpacity={0.8}
              >
                <Text style={styles.selectorText}>{getBadgeText()}</Text>
                <Animated.View
                  style={{ transform: [{ rotate: animatedRotation }] }}
                >
                  <Ionicons
                    name="chevron-down"
                    size={16}
                    color={Colors.lightGrayColor}
                  />
                </Animated.View>
              </TouchableOpacity>

              {/* Expanded Menu (slides upward) */}
              <Animated.View
                pointerEvents={isExpanded ? "auto" : "none"}
                style={[
                  styles.menuContainer,
                  {
                    opacity: animatedOpacity,
                    transform: [
                      { translateY: animatedTranslateY },
                      { scale: animatedScale },
                    ],
                  },
                ]}
              >
                {/* Invisible bridge to cover gap between menu and button */}
                <View style={styles.gapBridge} />

                <View style={styles.menuHeader}>
                  <Text style={styles.menuTitle}>
                    {t("filterWorkouts-string").toUpperCase()}
                  </Text>
                  {!isAllSelected && !isOnlySlotsSelected && (
                    <TouchableOpacity
                      onPress={handleShowAll}
                      style={[
                        styles.showAllButton,
                        isShowAllHovered && styles.showAllButtonHovered,
                      ]}
                      activeOpacity={0.8}
                      onMouseEnter={() =>
                        !isMobile && setIsShowAllHovered(true)
                      }
                      onMouseLeave={() => setIsShowAllHovered(false)}
                    >
                      <Text style={styles.showAllText}>{t("all-string")}</Text>
                    </TouchableOpacity>
                  )}
                </View>

                <View style={styles.checkboxList}>
                  {Array.from({ length: maxWorkoutPosition }, (_, i) => {
                    const position = i + 1;
                    const isSelected =
                      isAllSelected ||
                      (selectedPositions?.has(position) ?? false);
                    const isHovered = hoveredCheckbox === position;
                    const isDisabled = isOnlySlotsSelected;

                    return (
                      <TouchableOpacity
                        key={position}
                        style={[
                          styles.checkboxItem,
                          isHovered &&
                            !isSelected &&
                            styles.checkboxItemHovered,
                          isSelected && styles.checkboxItemSelected,
                          isHovered &&
                            isSelected &&
                            styles.checkboxItemHoveredSelected,
                          isDisabled && styles.checkboxItemDisabled,
                        ]}
                        onPress={() => handlePositionToggle(position)}
                        activeOpacity={isDisabled ? 1 : 0.7}
                        onMouseEnter={() =>
                          !isMobile &&
                          !isDisabled &&
                          setHoveredCheckbox(position)
                        }
                        onMouseLeave={() => setHoveredCheckbox(null)}
                      >
                        <View
                          style={[
                            styles.checkboxIconContainer,
                            isHovered && styles.checkboxIconContainerHovered,
                            isSelected && styles.checkboxIconContainerSelected,
                            isDisabled && styles.checkboxIconContainerDisabled,
                          ]}
                        >
                          {isSelected && (
                            <Ionicons
                              name="checkmark"
                              size={12}
                              color={Colors.whiteColor}
                            />
                          )}
                        </View>
                        <Text
                          style={[
                            styles.checkboxLabel,
                            isSelected && styles.checkboxLabelSelected,
                            isDisabled && styles.checkboxLabelDisabled,
                          ]}
                        >
                          {t("workoutWithNumber-string", { number: position })}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>

                {showOnlyProtocolSlotsToggle && (
                  <>
                    <View style={styles.sectionDivider} />
                    <TouchableOpacity
                      style={[
                        styles.checkboxItem,
                        isOnlySlotsHovered &&
                          !isOnlySlotsSelected &&
                          styles.checkboxItemHovered,
                        isOnlySlotsSelected && styles.checkboxItemSelected,
                        isOnlySlotsHovered &&
                          isOnlySlotsSelected &&
                          styles.checkboxItemHoveredSelected,
                      ]}
                      onPress={handleOnlySlotsToggle}
                      activeOpacity={0.7}
                      onMouseEnter={() =>
                        !isMobile && setIsOnlySlotsHovered(true)
                      }
                      onMouseLeave={() => setIsOnlySlotsHovered(false)}
                    >
                      <View
                        style={[
                          styles.checkboxIconContainer,
                          isOnlySlotsHovered &&
                            styles.checkboxIconContainerHovered,
                          isOnlySlotsSelected &&
                            styles.checkboxIconContainerSelected,
                        ]}
                      >
                        {isOnlySlotsSelected && (
                          <Ionicons
                            name="checkmark"
                            size={12}
                            color={Colors.whiteColor}
                          />
                        )}
                      </View>
                      <Text
                        style={[
                          styles.checkboxLabel,
                          isOnlySlotsSelected && styles.checkboxLabelSelected,
                        ]}
                      >
                        {t("onlySlots-string")}
                      </Text>
                    </TouchableOpacity>
                  </>
                )}
              </Animated.View>
            </View>
          </LinearGradient>
        </View>
      </View>
    </View>
  );
});

const CLIPBOARD_TOP_GAP_PX = 24;
const CLIPBOARD_MENU_BOTTOM_OFFSET_PX = 100;
const CLIPBOARD_HEADER_OFFSET_PX = 56;
const CLIPBOARD_MENU_MAX_HEIGHT = `calc(100vh - ${CLIPBOARD_TOP_GAP_PX}px - ${CLIPBOARD_MENU_BOTTOM_OFFSET_PX}px)`;
const CLIPBOARD_CONTENT_MAX_HEIGHT = `calc(100vh - ${CLIPBOARD_TOP_GAP_PX}px - ${CLIPBOARD_MENU_BOTTOM_OFFSET_PX}px - ${CLIPBOARD_HEADER_OFFSET_PX}px)`;

const styles = StyleSheet.create({
  root: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 50,
  },
  mobileBackdrop: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 40,
    backgroundColor: "transparent",
  },
  container: {
    position: "absolute",
    bottom: 60,
    left: 0,
    zIndex: 50,
    alignItems: "flex-start",
  },
  toolbarWrapper: {
    borderRadius: 24, // Pill shape
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.1)", // Subtle border
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2, // Increased shadow opacity
    shadowRadius: 12,
    elevation: 4,
  },
  toolbarGradient: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 4,
    paddingVertical: 3,
    borderRadius: 24, // Rounded corners
    // gap: 8, // Removed gap in favor of divider
  },
  clipboardWrapper: {
    position: "relative",
    zIndex: 10,
  },
  clipboardContainer: {
    alignItems: "center",
    justifyContent: "center",
    height: 32, // Match button height for perfect alignment
    minWidth: 0, // Allow width to collapse to 0
  },
  clipboardButton: {
    width: 32,
    height: 32,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16, // Circle
    // backgroundColor: Colors.lightGrayColor, // Removed background
  },
  clipboardIconStack: {
    width: 18,
    height: 18,
    alignItems: "center",
    justifyContent: "center",
  },
  clipboardPulseRing: {
    position: "absolute",
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 1.5,
    borderColor: Colors.bubbleColor,
  },
  expandCollapseButton: {
    width: 32,
    height: 32,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
  },
  verticalDivider: {
    width: 1,
    height: 20,
    backgroundColor: "rgba(255,255,255,0.2)", // Semi-transparent white
    marginHorizontal: 4,
  },
  selectorContainer: {
    position: "relative",
    zIndex: 10,
  },
  selectorButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    // backgroundColor: Colors.hoveredGray, // Removed background
  },
  selectorText: {
    fontSize: 13,
    fontWeight: "500",
    color: Colors.whiteColor, // White text
  },
  menuContainer: {
    position: "absolute",
    bottom: 40, // 8px gap from toolbar button
    left: -12, // Align with container
    backgroundColor: "#3A3A3C", // Dark background matching toolbar
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.1)", // Subtle border
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 24,
    elevation: 8,
    minWidth: 220,
    padding: 12,
  },
  clipboardMenuContainer: {
    left: 0, // Align with clipboard button
    minWidth: 280,
    maxHeight: CLIPBOARD_MENU_MAX_HEIGHT,
    zIndex: 1000, // Ensure it appears above other elements
    // Make sure menu is visible when expanded
  },
  clipboardContent: {
    maxHeight: CLIPBOARD_CONTENT_MAX_HEIGHT,
    minHeight: 0,
    scrollbarWidth: "thin",
    scrollbarColor: `${Colors.darkgrayColor} transparent`,
  },
  emptyClipboardText: {
    fontSize: 12,
    color: Colors.lightGrayColor,
    fontStyle: "italic",
    textAlign: "center",
    paddingVertical: 12,
  },
  clipboardSection: {
    gap: 8,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: "600",
    color: Colors.lightGrayColor,
    marginBottom: 4,
  },
  // Invisible bridge to cover the gap between menu and button
  gapBridge: {
    position: "absolute",
    bottom: -12, // Extend downwards to cover 8px gap
    left: 0,
    right: 0,
    height: 8,
    backgroundColor: "transparent",
    zIndex: -1,
  },
  menuHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.12)", // Subtle border for dark background
  },
  sectionDivider: {
    height: 1,
    backgroundColor: "rgba(255,255,255,0.12)",
    marginVertical: 8,
  },
  closeButton: {
    paddingVertical: 4,
    paddingHorizontal: 4,
    borderRadius: 6,
    backgroundColor: "rgba(255,255,255,0.1)", // Subtle background
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.2)", // Visible border to indicate clickability
    alignItems: "center",
    justifyContent: "center",
  },
  closeButtonHovered: {
    backgroundColor: "rgba(255,255,255,0.18)", // More visible on hover
    borderColor: "rgba(255,255,255,0.3)", // Brighter border on hover
  },
  menuTitle: {
    fontSize: 11,
    fontWeight: "600",
    color: Colors.lightGrayColor, // Light text for dark background
    letterSpacing: 0.5,
  },
  showAllButton: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
    backgroundColor: "rgba(255,255,255,0.1)", // Subtle background
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.2)", // Visible border to indicate clickability
  },
  showAllButtonHovered: {
    backgroundColor: "rgba(255,255,255,0.18)", // More visible on hover
    borderColor: "rgba(255,255,255,0.3)", // Brighter border on hover
  },
  showAllText: {
    fontSize: 11,
    fontWeight: "600",
    color: Colors.whiteColor, // White text for dark background
  },
  checkboxList: {
    gap: 4,
  },
  checkboxItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 8,
    gap: 10,
    // transition: "background-color 0.2s", // Web only
  },
  checkboxItemDisabled: {
    opacity: 0.5,
  },
  checkboxItemHovered: {
    backgroundColor: "rgba(255,255,255,0.05)", // Subtle hover background
  },
  checkboxItemSelected: {
    backgroundColor: "rgba(255,255,255,0.08)", // Subtle background for dark theme
  },
  checkboxItemHoveredSelected: {
    backgroundColor: "rgba(255,255,255,0.12)", // Slightly brighter when hovered and selected
  },
  checkboxIconContainer: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: "rgba(255,255,255,0.3)", // Light border for dark background
    backgroundColor: "transparent", // Transparent for dark background
    alignItems: "center",
    justifyContent: "center",
  },
  checkboxIconContainerDisabled: {
    borderColor: "rgba(255,255,255,0.2)",
  },
  checkboxIconContainerHovered: {
    borderColor: Colors.bubbleColor,
  },
  checkboxIconContainerSelected: {
    backgroundColor: Colors.bubbleColor,
    borderColor: Colors.bubbleColor,
  },
  checkboxLabel: {
    fontSize: 12,
    color: Colors.lightGrayColor, // Light text for dark background
    fontWeight: "500",
  },
  checkboxLabelDisabled: {
    color: "rgba(255,255,255,0.45)",
  },
  checkboxLabelSelected: {
    color: Colors.whiteColor, // White text for dark background
    fontWeight: "500",
  },
  // Sets table styles (copied/adapted from ExerciseCard)
  setsTable: {
    gap: 4,
  },
  tableHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 0,
    width: "100%",
  },
  tableHeaderCameraCell: {
    width: 24,
    alignItems: "center",
  },
  tableHeaderPriorityCell: {
    width: 24,
    alignItems: "center",
  },
  tableHeaderPriorityText: {
    fontSize: 10,
    fontWeight: "400",
    color: Colors.lightGrayColor,
    textAlign: "center",
  },
  tableHeaderText: {
    fontSize: 10,
    fontWeight: "400",
    color: Colors.lightGrayColor, // Adapted for dark theme
    textAlign: "center",
  },
  clipboardSetRowWrapper: {
    marginBottom: 0,
  },
  clipboardExerciseWrapper: {
    opacity: 0.9,
  },
});

export default BlockViewOverlay;
